import React, { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import StaticPage from "../../common/StaticPage";
import { useNavigate } from "react-router";
import moment from "moment";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { getCustomerName, hasManagerAccess, shouldCollectSalesTax } from "../../common/util";
import { canAccessPath, canEditPath } from "../../common/access-control";
import { UserContext } from "../../context/UserContext";
import ShareButton from "../../common/component/ShareButton";
import QuoteStatusHeader from "../component/QuoteStatusHeader";
import CustomerDetailsButton from "../../common/component/CustomerDetailsButton";
import LineItemV2 from "../../common/component/lineitem/LineItemV2";
import CustomerContactInfoButton from "../../common/component/CustomerContactInfoButton";
import RequestSummary from "../../request/components/RequestSummary";
import { Timeline, TimelineContent, TimelineConnector, TimelineSeparator } from "@mui/lab";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import CancelQuote from "./CancelQuote";
import { ExpandMoreOutlined } from "@mui/icons-material";
import UserAvatar from "../../common/UserAvatar";
import QuoteFulfillment from "../component/QuoteFulfillment";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import SendQuoteHeader from "../../baker/components/SendQuoteHeader";
import BakerChat from "../component/action/BakerChat";
import { withAppendedItemImage } from "../../common/chat/useAddChatImageToItem";
import SendQuote from "../component/SendQuote";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { CheckOutlined } from "@mui/icons-material/";
import IconText from "../../common/IconText";
import CustomLineItemEntryPopup from "../../common/component/lineitem/CustomLineItemEntryPopup";
import currency from "currency.js";
import RichTextNotes from "../../common/component/RichTextNotes";

export default function Quote(props) {
  const { user } = useContext(UserContext);
  let location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const { hash } = useLocation();

  const { reqId } = useParams();
  const [request, setRequest] = useState();
  const [quote, setQuote] = useState();
  const [mode, setMode] = useState("view");
  const [history, setHistory] = useState("");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    setRequest("");
    setQuote("");
    setMode("view");

    bkstApi(`/my/quote/${reqId}`).then((res) => {
      if (hash === "#chat") {
        // check if order has been placed
        if (res.data?.request?.orderId) {
          navigate(`/order/${res.data.request.orderId}#chat`);
        }
      }

      setRequest(res.data.request);
      setQuote(res.data.quote);
      setMode(res.data.quote.status === "pending" && !res.data.quote.items ? "edit" : "view");
      setLoading(false);
    });
  }, [reqId, location.key]);

  const extendExpiration = () => {
    bkstApi.put(`/my/quote/${request.id}/exdate`, {}).then((res) => {
      bkstApi(`/my/quote/${request.id}`).then((res) => {
        navigate(0);
      });
    });
  };

  const saveProductionNotes = (production) => {
    bkstApi.put(`/my/quote/${request.id}/production`, { production }).then((res) => {
      setQuote({ ...quote, production });
    });
  };

  const handleImageAdded = (itemIndex, url) => {
    setQuote((prev) => {
      if (!prev?.items) return prev;
      return { ...prev, items: withAppendedItemImage(prev.items, itemIndex, url) };
    });
  };

  const getHistory = () => {
    return bkstApi(`/my/quote/${reqId}/history`).then((res) => {
      let arr = [];
      for (let o of res.data.history) {
        if (!o.updTs) {
          o.updTs = o.ts._seconds * 1000;
        }
        arr.push(o);
      }
      arr.push(quote);
      arr.sort((o1, o2) => +o2.updTs - +o1.updTs);

      setHistory(arr);
    });
  };

  if (loading) {
    return <LinearProgress />;
  } else if (!request) {
    return (
      <StaticPage
        title="Closed"
        desc="Sorry, this quote request is no longer available. Either the customer has already placed an order or the request date has passed."
        icon={UpdateOutlinedIcon}
        href="/dashboard"
        btnText="View Dashboard"
      ></StaticPage>
    );
  } else if (request) {
    const isManager = hasManagerAccess(user.role);
    const quotePath = `/quote/${reqId}`;
    const canEditQuote = canEditPath(user, quotePath);

    let { status, exdate } = quote;
    let expiration = exdate && exdate.includes("T") ? exdate : `${exdate}T23:59`;

    if (status === "active" && moment(expiration).diff(moment(), "minutes") <= 0) {
      status = "expired";
    } else if (status === "block") {
      status = "unavailable";
    }

    const name = getCustomerName({
      firstName: request.firstName,
      lastName: request.lastName,
      name: request.name,
    });

    const historyWidget = (
      <Accordion
        elevation={0}
        onChange={(e, expanded) => {
          if (expanded) {
            getHistory();
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>History</AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box>
            {history && (
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {history.map((o) => {
                  return (
                    <TimelineItem>
                      <TimelineSeparator>
                        <UserAvatar uid={o.uid} />
                        <TimelineConnector />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent mb={4} sx={{ backgroundColor: "#F7F7F7" }}>
                        <Box py={1}>
                          <Typography variant="overline" display="block">
                            {moment(o.updTs).format("LLLL")}
                          </Typography>
                          {o.status === "pending" || o.status === "unavailable" || !o.items ? (
                            <Paper elevation={0} my={1}>
                              <Box p={2}>
                                <RequestSummary request={request} />
                              </Box>
                            </Paper>
                          ) : (
                            <Paper elevation={0}>
                              <Box p={2} my={0.5}>
                                {o.items.map((item, idx) => (
                                  <Box key={idx} py={1}>
                                    <LineItemV2 item={item} view="min" />
                                  </Box>
                                ))}
                                <Box my={1}>
                                  <Divider />
                                </Box>
                                <QuoteFulfillment quote={o} />
                              </Box>
                            </Paper>
                          )}
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );

    const ADD_QUOTE = (
      <CustomLineItemEntryPopup
        idx={0}
        mode="add"
        cta="Add Quote"
        request={request}
        isQuote={true}
        onChange={(o) => {
          let q = { ...quote, items: [o] };
          q.fulfillment = {
            date: request.date,
            option: {
              pickup: {},
              delivery: {
                type: user.config?.fulfillment?.delivery?.["third-party"] || "no",
                location: request.eventLocation,
                includeFees: user.config?.transferFees === "false" ? false : true,
              },
            },
          };
          setMode("edit");
          setQuote(q);
        }}
      />
    );

    if (mode === "edit" && !quote.items) {
      if (canEditQuote) {
        return (
          <Container maxWidth="md">
            <Box my={2}>
              <QuoteStatusHeader status={status} request={request} quote={quote} />
            </Box>
            <Box my={2}>
              <SendQuoteHeader
                status={status}
                request={request}
                quote={quote}
                onImageAdded={handleImageAdded}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "15px" }}>
              {canEditQuote && <CancelQuote request={request} cta="Reject Quote" />}
              {ADD_QUOTE}
            </Box>
          </Container>
        );
      } else {
        return (
          <StaticPage
            title="Access Denied"
            desc="Sorry, you're not authorized to view this page."
            icon={PanToolOutlinedIcon}
            href="/dashboard"
            btnText="View Dashboard"
          ></StaticPage>
        );
      }
    } else if (mode === "edit" && quote.items) {
      if (canEditQuote) {
        return (
          <Container maxWidth="md">
            <Box my={2}>
              <SendQuote
                request={request}
                quote={quote}
                items={quote.items}
                fulfillment={quote.fulfillment}
              />
            </Box>
          </Container>
        );
      } else {
        return (
          <StaticPage
            title="Access Denied"
            desc="Sorry, you're not authorized to view this page."
            icon={PanToolOutlinedIcon}
            href="/dashboard"
            btnText="View Dashboard"
          ></StaticPage>
        );
      }
    } else {
      const daysToEvent = moment(quote?.fulfillment?.date || request.date).diff(moment(), "days");

      let cta = "";
      if (quote.status === "paid" && request.orderId) {
        cta = (
          <Button
            color="primary"
            variant="contained"
            component={RouterLink}
            to={`/order/${request.orderId}`}
            fullWidth
          >
            View Order
          </Button>
        );
      } else if (status === "expired" && daysToEvent >= 0) {
        cta = (
          <Box mt={2} sx={{ textAlign: "center" }}>
            <Button color="primary" variant="contained" onClick={extendExpiration} fullWidth>
              Extend Expiration
            </Button>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Extend quote expiration by the recommended duration based on the event date
              </Typography>
            </Box>
          </Box>
        );
      } else if (
        request.src === "direct" &&
        request.status !== "closed" &&
        (quote.status === "active" || quote.status === "expired")
      ) {
        cta = (
          <Box mt={2}>
            <Box textAlign={"center"}>
              <Button
                component={RouterLink}
                to="/create"
                state={{
                  customer: {
                    firstName: request.firstName,
                    lastName: request.lastName,
                    name: request.name,
                    phone: request.phone,
                    email: request.email,
                  },
                  items: quote.items,
                  fulfillment: { date: quote.fulfillment.date },
                  adjustments: quote.adjustments,
                  reqId: request.id,
                  settings: quote.settings,
                }}
                fullWidth
                variant="contained"
                color="primary"
              >
                Create Order
              </Button>
              <Typography variant="caption" color="textSecondary">
                Convert this quote to a confirmed order by taking a payment. Ideal for customers
                in-store or over the phone.
              </Typography>
            </Box>
          </Box>
        );
      }

      return (
        <Container maxWidth="md">
          <Box my={4}>
            <Box my={4}>
              <QuoteStatusHeader status={status} request={request} quote={quote} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ display: "flex", gap: "7px", alignItems: "center" }}>
                {request.customer?.id ? (
                  <CustomerDetailsButton customer={request.customer} />
                ) : (
                  <CustomerContactInfoButton
                    customer={{
                      firstName: request.firstName,
                      lastName: request.lastName,
                      name: request.name,
                    }}
                  />
                )}
              </Box>
              {request.src === "direct" && (isManager || canEditQuote) && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                    flexGrow: "1",
                    justifyContent: "flex-end",
                  }}
                >
                  {isManager && quote.status !== "paid" && (
                    <ShareButton type="quote" link={`/quote/${reqId}`} />
                  )}
                  {isManager && (
                    <BakerChat
                      request={request}
                      quote={quote}
                      status={status}
                      onImageAdded={handleImageAdded}
                    />
                  )}
                  {canEditQuote &&
                    canAccessPath(user, `/quote/${request.id}/edit`) &&
                    quote.items &&
                    quote.status !== "paid" && (
                    <IconButton
                      color="secondary"
                      component={RouterLink}
                      to={`/quote/${request.id}/edit`}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
            {quote.items ? (
              <Box>
                <Paper elevation={0}>
                  <Box px={2} py={2}>
                    {quote.items.map((item, idx) => (
                      <Box key={idx} py={1}>
                        <LineItemV2 item={item} view="min" />
                      </Box>
                    ))}
                    <Box my={2}>
                      <Divider />
                    </Box>
                    <QuoteFulfillment quote={quote} />
                    {quote.adjustments?.discount?.value > 0 && (
                      <Box mt={2}>
                        <Divider />
                        <Box pt={2}>
                          <IconText
                            icon={CheckOutlined}
                            text={`Applied Discount: ${currency(quote.adjustments?.discount?.value || 0).format()}`}
                            variant="body1"
                          />
                        </Box>
                      </Box>
                    )}

                    {shouldCollectSalesTax(quote) && (
                      <Box mt={2}>
                        <Divider />
                        <Box pt={2}>
                          <IconText
                            icon={CheckOutlined}
                            text={"Collect Sales Tax"}
                            variant="body1"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
                <Box my={2}>{cta}</Box>
              </Box>
            ) : (
              <Box>
                <RequestSummary request={request} />
                <Box my={2}>{ADD_QUOTE}</Box>
              </Box>
            )}
            <Box my={6}>
              {request.src === "direct" && request.status !== "paid" && (
                <Accordion elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                    Other Actions
                  </AccordionSummary>
                  <AccordionDetails>
                    {canEditQuote && (
                      <Box px={2} py={2}>
                        <CancelQuote request={request} />
                        <Typography variant="caption" color="textSecondary">
                          Canceling a quote removes it from your dashboard. Active quotes are
                          expired & can no longer be booked by customer.
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
              {isManager && (
                <Accordion elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                    Production Notes
                  </AccordionSummary>
                  <AccordionDetails>
                    <RichTextNotes
                      content={quote.production?.notes || ""}
                      placeholder="Add private notes for your staff. This is for internal use only and will not be visible to the customer. Once order is placed, this will carry over to the order."
                      onUpdate={(content) => {
                        saveProductionNotes({ notes: content });
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
              {isManager && historyWidget}
            </Box>
          </Box>
        </Container>
      );
    }
  }
}
